// ═══════════════════════════════════════════════════════════════
// APRENDIZAJES DE CASO — KCS (Knowledge-Centered Service)
// ═══════════════════════════════════════════════════════════════
//
// Cada caso cerrado con éxito puede generar un "aprendizaje" — una entrada
// en la KB interna que captura el problema + la resolución + qué se aprendió.
// Los aprendizajes alimentan el contexto cuando llega un caso nuevo similar
// (matcheo por categoría + tags + tipo).
//
// Esto NO es lo mismo que `empresa._aprendizajes` que vive en `empresas/{id}`
// y es un rollup estadístico de rentabilidad. Acá hablamos de KB textual.
//
// Colección: aprendizajesCaso/{id} (top-level con empresaId)
//
// Schema:
//   {
//     empresaId,
//     casoIdOrigen,        // ID del caso del que nació
//     numeroCasoOrigen,    // CASE-2026-00042 para display
//
//     titulo,              // resumen corto (lo que aparece en la lista)
//     contexto,            // descripción del problema
//     resolucion,          // qué se hizo concretamente
//     aprendizaje,         // qué aprendimos / qué evitar / qué replicar
//
//     // Para matcheo de relevancia (heredado del caso origen)
//     tipo,                // tipoCaso codigo (ej: 'reclamo-ml')
//     categoria,           // 'logistica', 'producto', etc.
//     tags: [],
//
//     // Visibilidad
//     publicado,           // false = draft solo creador, true = visible a la empresa
//     visibleEnPlataforma, // futuro: si Milbrands quiere usar como knowledge base global anonimizada
//
//     // Tracking
//     creadoPor, creadoEn, actualizadoEn, version, activo,
//   }
// ═══════════════════════════════════════════════════════════════

import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, query, where, limit, orderBy,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { forCreate, forUpdate, sanitize, requireString } from './dbHelpers'

const COL = 'aprendizajesCaso'

// ──────── ABM ────────

export async function listarAprendizajes({ empresaId, soloPublicados = true, max = 50 } = {}) {
  if (!empresaId) return []
  let q = query(
    collection(db, COL),
    where('empresaId', '==', empresaId),
    where('activo', '==', true),
    orderBy('creadoEn', 'desc'),
    limit(max),
  )
  const snap = await getDocs(q)
  let list = snap.docs.map(docConId)
  if (soloPublicados) list = list.filter(a => a.publicado !== false)
  return list
}

export async function getAprendizaje(aprendizajeId) {
  const snap = await getDoc(doc(db, COL, aprendizajeId))
  return snap.exists() ? docConId(snap) : null
}

/**
 * Crea un aprendizaje desde un caso cerrado.
 *
 * @param {Object} caso — doc del caso (incluye empresaId, tipo, categoria, etc.)
 * @param {Object} data — { titulo, contexto, resolucion, aprendizaje, tags?, publicado? }
 * @param {string} autor — userId del que lo crea
 * @returns {string} aprendizajeId
 */
export async function crearAprendizajeDesdeCaso(caso, data, autor) {
  if (!caso?.empresaId) throw new Error('caso.empresaId requerido')

  const payload = sanitize({
    casoIdOrigen: caso.id || null,
    numeroCasoOrigen: caso.numero || null,
    titulo: requireString(data.titulo, 'titulo'),
    contexto: data.contexto || caso.descripcion || '',
    resolucion: data.resolucion || '',
    aprendizaje: data.aprendizaje || '',
    tipo: caso.tipo || null,
    categoria: caso.categoria || null,
    tags: Array.isArray(data.tags) ? data.tags : (caso.tags || []),
    publicado: data.publicado !== false,
    visibleEnPlataforma: false,  // future: opt-in para KB global Milbrands
    creadoPor: autor || null,
    version: 1,
    activo: true,
  })

  const ref = await addDoc(collection(db, COL), forCreate(payload, caso.empresaId))
  return ref.id
}

export async function actualizarAprendizaje(aprendizajeId, data) {
  const { id: _id, empresaId, casoIdOrigen, ...safe } = data
  await updateDoc(doc(db, COL, aprendizajeId), forUpdate(sanitize(safe)))
}

// ──────── Búsqueda de relevantes ────────

/**
 * Busca aprendizajes relevantes para un caso dado. Match por:
 *   1. Mismo tipo (boost +3)
 *   2. Misma categoría (boost +2)
 *   3. Tags en común (boost +1 por tag)
 *
 * Filtra solo publicados de la empresa del caso. Retorna ordenado por score.
 *
 * @param {Object} caso
 * @param {number} maxResultados
 * @returns {Promise<Array<{id, score, ...aprendizaje}>>}
 */
export async function buscarRelevantes(caso, maxResultados = 5) {
  if (!caso?.empresaId) return []
  const todos = await listarAprendizajes({ empresaId: caso.empresaId, soloPublicados: true, max: 200 })
  if (!todos.length) return []

  // No incluir el aprendizaje generado del propio caso (si hay)
  const candidatos = todos.filter(a => a.casoIdOrigen !== caso.id)

  const tagsCaso = new Set(caso.tags || [])

  const scored = candidatos.map(a => {
    let score = 0
    if (a.tipo && a.tipo === caso.tipo) score += 3
    if (a.categoria && a.categoria === caso.categoria) score += 2
    for (const t of (a.tags || [])) {
      if (tagsCaso.has(t)) score += 1
    }
    return { ...a, score }
  })

  return scored
    .filter(a => a.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResultados)
}

// ──────── Window debug ────────
if (typeof window !== 'undefined') {
  window.__aprendizajesCaso = {
    listarAprendizajes,
    getAprendizaje,
    crearAprendizajeDesdeCaso,
    actualizarAprendizaje,
    buscarRelevantes,
  }
}
